import { useState, useRef, useEffect } from 'react';

export interface ChatMessage {
  id: number;
  text: string;
  sender: 'me' | 'them';
  time: string;
}

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  messages: ChatMessage[];
  onSendMessage: (text: string) => void;
}

const ChatPanel = ({ isOpen, onClose, messages, onSendMessage }: ChatPanelProps) => {
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  const sendMessage = () => {
    const text = input.trim();
    if (!text) return;

    onSendMessage(text);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div
      className={`absolute top-0 right-0 h-full w-80 flex flex-col bg-black/75 backdrop-blur-2xl border-l border-white/8 z-20 transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
        <div className="flex items-center gap-2">
          <i className="ri-chat-3-line text-cyan-400" />
          <span className="text-white font-semibold text-sm">Sohbet</span>
        </div>
        <button
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 text-white/50 hover:text-white transition-all cursor-pointer"
        >
          <i className="ri-close-line text-sm" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3 scrollbar-thin">
        {messages.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-center text-white/25 text-sm px-6">
            Henüz mesaj yok. Sohbeti başlatabilirsin.
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex flex-col gap-1 ${msg.sender === 'me' ? 'items-end' : 'items-start'}`}
            >
              <div
                className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.sender === 'me'
                    ? 'bg-gradient-to-br from-cyan-500 to-violet-600 text-white rounded-br-sm'
                    : 'bg-white/8 border border-white/10 text-white/85 rounded-bl-sm'
                }`}
              >
                {msg.text}
              </div>
              <span className="text-white/25 text-xs px-1">{msg.time}</span>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      <div className="px-4 py-4 border-t border-white/8">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 focus-within:border-cyan-500/40 transition-colors">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Mesaj yaz..."
            maxLength={300}
            className="flex-1 bg-transparent text-white text-sm placeholder-white/25 focus:outline-none"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim()}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-violet-600 text-white hover:from-cyan-400 hover:to-violet-500 transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="ri-send-plane-line text-sm" />
          </button>
        </div>
        <p className="text-white/20 text-xs mt-2 text-right">{input.length}/300</p>
      </div>
    </div>
  );
};

export default ChatPanel;